function normalizeText(value) {
  return String(value || '')
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '');
}

function normalizeOrderValue(value) {
  const digits = String(value ?? '')
    .trim()
    .replace(/\D/g, '');

  return digits || '1';
}

function normalizeEvidenceType(value) {
  return String(value || '')
    .trim()
    .toUpperCase();
}

function normalizeEvidenceProcessCode(value) {
  const raw = String(value || '').trim();
  if (!raw) return '';

  const upper = raw.toUpperCase();
  const advisorMatch = upper.match(/^ASES(\d+)$/);
  if (advisorMatch) return `As${advisorMatch[1]}`;

  if (upper.startsWith('IE')) return upper;

  const stripped = upper.replace(/^C\d+/i, '');
  return stripped || upper;
}

function formatNumericValue(value, compact = false) {
  const normalized = String(value ?? '').trim();
  if (!compact) {
    return normalized;
  }

  const numeric = Number(normalized);
  if (!Number.isInteger(numeric) || numeric < 0) {
    return normalized;
  }

  return numeric.toString(36).toUpperCase();
}

function extractProcessMeta(...candidates) {
  for (const candidate of candidates) {
    const normalized = normalizeText(candidate);
    const match = normalized.match(/(PF|NDN|DI)(\d+)/);

    if (!match) {
      continue;
    }

    return {
      scope: match[1] === 'NDN' ? 'N' : match[1] === 'DI' ? 'D' : 'P',
      processNumber: match[2]
    };
  }

  return null;
}

function extractGroupMeta(...candidates) {
  for (const candidate of candidates) {
    const normalized = normalizeText(candidate);
    const match = normalized.match(/(GD|G)(\d+)$/);

    if (!match) {
      continue;
    }

    return {
      type: match[1] === 'GD' ? 'D' : 'G',
      groupNumber: match[2]
    };
  }

  return null;
}

function buildCompactSuffix({
  orderValue,
  schoolCode,
  groupCode,
  compactNumbers = false
}) {
  const processMeta = extractProcessMeta(groupCode, schoolCode);
  const groupMeta = extractGroupMeta(groupCode, schoolCode);

  if (!processMeta || !groupMeta) {
    return '';
  }

  return [
    formatNumericValue(normalizeOrderValue(orderValue), compactNumbers),
    processMeta.scope,
    formatNumericValue(processMeta.processNumber, compactNumbers),
    groupMeta.type,
    formatNumericValue(groupMeta.groupNumber, compactNumbers)
  ].join('');
}

function stripFileExtension(value) {
  const raw = String(value || '').trim().split(/[\\/]/).pop() || '';
  return raw.replace(/\.[a-z0-9]+$/i, '');
}

export function formatDateForEvidenceFile(value) {
  const raw = String(value || '').trim();
  if (!raw) return '';

  const normalized = raw.split('T')[0];
  if (!normalized) return '';

  const parts = normalized.split('-');
  if (parts.length === 3) {
    const [year, month, day] = parts;
    return `${day}${month}${year.slice(-2)}`;
  }

  return normalized.replace(/\D/g, '').slice(0, 6);
}

export function buildEvidenceDownloadName({
  prefix,
  evidenceType = '',
  municipalityId,
  meetingDate,
  orderValue = '1',
  schoolCode = '',
  groupCode = '',
  extension = 'jpg',
  fallbackFileName = '',
  fallbackIndex = 1
}) {
  const normalizedPrefix = String(prefix || '').trim();
  const normalizedEvidenceType = normalizeEvidenceType(evidenceType);
  const normalizedMunicipalityId = String(municipalityId ?? '').trim();
  const dateCode = formatDateForEvidenceFile(meetingDate);
  const normalizedProcessCode = normalizeEvidenceProcessCode(schoolCode);

  if (normalizedPrefix === 'Req' || normalizedPrefix === 'Refri') {
    const logisticsSuffix = buildCompactSuffix({
      orderValue,
      schoolCode,
      groupCode,
      compactNumbers: true
    });

    if (dateCode && logisticsSuffix) {
      return `${normalizedPrefix}_${dateCode}_${logisticsSuffix}.${extension}`;
    }
  }

  if (
    normalizedPrefix &&
    normalizedMunicipalityId &&
    dateCode &&
    normalizedProcessCode
  ) {
    if (normalizedEvidenceType === 'CONC' || normalizedEvidenceType === 'EXP') {
      return `${normalizedPrefix}${normalizedMunicipalityId}_${dateCode}_co${normalizedProcessCode}.${extension}`;
    }

    if (normalizedEvidenceType === 'SOC') {
      return `${normalizedPrefix}${normalizedMunicipalityId}_${dateCode}_so${normalizedProcessCode}.${extension}`;
    }

    if (normalizedEvidenceType.startsWith('COP_')) {
      return `${normalizedPrefix}${normalizedMunicipalityId}_${dateCode}_e${normalizeOrderValue(orderValue)}${normalizedProcessCode}.${extension}`;
    }
  }

  const fullSuffix = buildCompactSuffix({
    orderValue,
    schoolCode,
    groupCode,
    compactNumbers: false
  });

  if (normalizedPrefix && normalizedMunicipalityId && dateCode && fullSuffix) {
    const fullBaseName = `${normalizedPrefix}${normalizedMunicipalityId}_${dateCode}_${fullSuffix}`;
    if (fullBaseName.length <= 20) {
      return `${fullBaseName}.${extension}`;
    }

    const compactSuffix = buildCompactSuffix({
      orderValue,
      schoolCode,
      groupCode,
      compactNumbers: true
    });

    if (compactSuffix) {
      const compactBaseName = `${normalizedPrefix}${normalizedMunicipalityId}_${dateCode}_${compactSuffix}`;
      if (compactBaseName.length <= 20) {
        return `${compactBaseName}.${extension}`;
      }
    }
  }

  const fallbackBaseName = stripFileExtension(fallbackFileName);
  if (fallbackBaseName) {
    return `${fallbackBaseName.slice(0, 20)}.${extension}`;
  }

  return `evidencia_${fallbackIndex}.${extension}`;
}
